import * as Api from "@api";
import * as C from "@components";
import Link from "next/link";
import React from "react";
import css from "./main-menu.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";

interface MainMenuProductProps {
  product: Api.ProductFull;
}

const DEFAULT_PRODUCT_PROPS = {
  width: 48,
  height: 80,
  link: "nav-thumb.png",
};

const CUSTOM_PRODUCT_PROPS: Record<
  string,
  {
    width?: number;
    height?: number;
    link?: string;
    styles?: Record<string, string>;
  }
> = {
  "active-sprout": { width: 52 },
  "active-axil-mini-air": { link: "nav-thumb.2.png" },
  "axil-s1-battery": { link: "navigation/singular-axil-s1-battery.png" },
  "offset-s1-battery": {
    link: "navigation/singular-offset-s1-battery.png",
    width: 130,
    height: 80,
  },
  "air-pod-postless": {
    link: "navigation/singular-air-pod-postless.png",
    width: 73,
    height: 100,
  },
  "pro-pod-ceramic": {
    link: "navigation/singular-pro-pod-ceramic.png",
    width: 73,
    height: 100,
  },
  "classic-pod-stainless": {
    link: "navigation/singular-classic-pod-stainless.png",
    width: 73,
    height: 100,
  },
  "singular-510-adapter": {
    link: "navigation/singular-510-adapter.png",
    width: 130,
    height: 180,
    styles: { height: "92px" },
  },
  "active-axil-edge": { link: "product-thumb.edge.png" },
  "active-axil-curve": { link: "product-thumb.curve.png" },
};

const MOBILE_CUSTOM_PRODUCT_PROPS: Record<
  string,
  {
    width?: number;
    height?: number;
    link?: string;
    styles?: Record<string, string>;
    className?: string;
  }
> = {
  "axil-s1-battery": {
    link: "navigation/singular-axil-s1-battery.png",
    width: 35,
    height: 70,
  },
  "offset-s1-battery": {
    link: "navigation/singular-offset-s1-battery.png",
    width: 73,
    height: 80,
  },
  "air-pod-postless": {
    link: "navigation/singular-air-pod-postless.png",
    width: 47,
    height: 80,
  },
  "pro-pod-ceramic": {
    link: "navigation/singular-pro-pod-ceramic.png",
    width: 47,
    height: 80,
    // className: "mx-2",
  },
  "classic-pod-stainless": {
    link: "navigation/singular-classic-pod-stainless.png",
    width: 47,
    height: 100,
  },
  "singular-510-adapter": {
    link: "navigation/singular-510-adapter.png",
    width: 47,
    height: 80,
    className: "scale-[1.7]",
  },
};

export const MainMenuProduct = ({
  product,
}: MainMenuProductProps): React.FunctionComponentElement<MainMenuProductProps> => {
  const { width, height, link, styles } =
    CUSTOM_PRODUCT_PROPS[product.slug] || DEFAULT_PRODUCT_PROPS;

  return (
    <Link className="relative grid grid-cols-2 md:grid-cols-1 items-center min-h-[131px] rounded-xl hover:bg-muted-tan" href={product.permalink ?? "#"}>
      <div className="relative w-full flex justify-center items-center max-h-[165px]">
        <VadImage
          src={`/images${product.permalink}/${
            link ?? DEFAULT_PRODUCT_PROPS.link
          }`}
          alt={product.title ?? product.slug}
          width={width ?? DEFAULT_PRODUCT_PROPS.width}
          height={height ?? DEFAULT_PRODUCT_PROPS.height}
          style={styles}
          className="hidden md:block"
        />
        <VadImage
          src={`/images${product.permalink}/${
            link ?? DEFAULT_PRODUCT_PROPS.link
          }`}
          alt={product.title ?? product.slug}
          width={
            MOBILE_CUSTOM_PRODUCT_PROPS[product.slug]?.width ??
            DEFAULT_PRODUCT_PROPS.width
          }
          height={
            MOBILE_CUSTOM_PRODUCT_PROPS[product.slug]?.height ??
            DEFAULT_PRODUCT_PROPS.height
          }
          style={MOBILE_CUSTOM_PRODUCT_PROPS[product.slug]?.styles ?? {}}
          className={`block md:hidden ${
            MOBILE_CUSTOM_PRODUCT_PROPS[product.slug]?.className ?? ""
          }`}
        />
      </div>
      <p className="w-full md:text-center text-sm font-medium">{product.title}</p>
    </Link>
  );
};
